import Box, { BoxProps } from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import {
  DatePickerProps,
  DatePicker as MUIDatePicker,
} from '@mui/x-date-pickers/DatePicker'
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar'
import dayjs from 'lib/dayjs'
import { FC, useState, useMemo, useEffect } from 'react'
import { TodayRounded } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'

interface IDatePicker extends DatePickerProps<dayjs.Dayjs> {
  date?: Date
  label: string
  disabled?: boolean
  renderDatePicker?: boolean
  onDateSelection?: (date: Date) => void
  required?: boolean
  errorMessage?: string
}

function CustomActionBar(props: PickersActionBarProps) {
  const { onAccept, onClear, onCancel, onSetToday } = props
  // Check if we're on a mobile device
  const isMobile = useMediaQuery('(max-width:600px)')

  // Return null on mobile devices
  if (isMobile) {
    return null
  }

  // Custom date navigation handlers
  const handleYesterday = () => {
    const yesterday = dayjs().subtract(1, 'day')
    if (onAccept) {
      onAccept()
      // We need to wait for the datepicker to process the acceptance before setting a new date
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('set-date', { detail: yesterday.toDate() })
        )
      }, 10)
    }
  }

  const handleTomorrow = () => {
    const tomorrow = dayjs().add(1, 'day')
    if (onAccept) {
      onAccept()
      // We need to wait for the datepicker to process the acceptance before setting a new date
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('set-date', { detail: tomorrow.toDate() })
        )
      }, 10)
    }
  }

  // Base button styles for desktop
  const baseButtonStyle = {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none' as const,
    height: '26px',
    borderRadius: '4px',
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      border: '1px solid #cbd5e1',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: '#e2e8f0',
    },
  }

  // Desktop version
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        top: '8px',
        left: 0,
        right: 0,
        zIndex: 1,
        gap: '4px',
      }}
    >
      {/* Yesterday Button */}
      <Button
        onClick={handleYesterday}
        sx={{
          ...baseButtonStyle,
          minWidth: 'auto',
          padding: '2px 6px',
        }}
        variant="outlined"
        disableElevation
        size="small"
      >
        Yesterday
      </Button>

      {/* Today Button */}
      <Button
        onClick={onSetToday}
        sx={{
          ...baseButtonStyle,
          minWidth: 'auto',
          padding: '2px 10px',
          backgroundColor: '#f8fafc',
          borderColor: '#94a3b8',
          color: '#475569',
          '&:hover': {
            backgroundColor: '#f1f5f9',
            borderColor: '#64748b',
          },
        }}
        variant="outlined"
        disableElevation
        size="small"
        startIcon={
          <TodayRounded
            sx={{
              fontSize: '0.75rem',
              marginRight: '2px',
            }}
          />
        }
      >
        Today
      </Button>

      {/* Tomorrow Button */}
      <Button
        onClick={handleTomorrow}
        sx={{
          ...baseButtonStyle,
          minWidth: 'auto',
          padding: '2px 6px',
        }}
        variant="outlined"
        disableElevation
        size="small"
      >
        Tomorrow
      </Button>
    </Box>
  )
}

const CustomDatePicker: FC<IDatePicker> = ({
  date,
  label,
  disabled,
  renderDatePicker = true,
  onDateSelection,
  required = false,
  minDate,
  errorMessage,
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(date)
  const [isFilled, setIsFilled] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')

  useEffect(() => {
    setIsFilled(selectedDate !== undefined)
  }, [selectedDate])

  // Add scroll event listener to close the date picker
  useEffect(() => {
    const handleScroll = (event: Event) => {
      // Check if the target is an Element before using closest
      const target = event.target
      const isCalendarScroll =
        target instanceof Element &&
        target.closest('.MuiDateCalendar-root') !== null

      if (isOpen && !isCalendarScroll) {
        setIsOpen(false)
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  // Add event listener for custom date navigation
  useEffect(() => {
    const handleSetDate = (event: Event) => {
      const customEvent = event as CustomEvent
      if (customEvent.detail) {
        setSelectedDate(customEvent.detail)
        onDateSelection && onDateSelection(customEvent.detail)
      }
    }

    document.addEventListener('set-date', handleSetDate)
    return () => {
      document.removeEventListener('set-date', handleSetDate)
    }
  }, [onDateSelection])

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedDate(date.toDate())
      onDateSelection && onDateSelection(date.toDate())
      setIsOpen(false)
    }
  }

  useMemo(() => {
    setSelectedDate(date)
  }, [date])

  return (
    <Box
      data-testid="custom-date-picker"
      aria-label="date-picker"
      display="flex"
      justifyContent={'flex-start'}
      alignItems={'center'}
      flexDirection={'column'}
      {...(props as BoxProps)}
    >
      {renderDatePicker ? (
        <MUIDatePicker
          label={label}
          value={selectedDate ? dayjs(selectedDate) : null}
          onChange={handleDateChange}
          disabled={disabled}
          minDate={minDate}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          slotProps={{
            textField: {
              sx: {
                backgroundColor: 'white',
                borderRadius: '4px',
              },
              onClick: () => setIsOpen(true),
            },
            actionBar: {
              actions: ['today'],
            },
            layout: {
              sx: {
                '& .MuiPickersLayout-contentWrapper': {
                  paddingBottom: 0,
                },
                '& .MuiDateCalendar-root': {
                  position: 'relative',
                  paddingTop: isMobile ? 0 : '40px',
                  maxHeight: 'none',
                  height: 'auto',
                },
                '& .MuiDayCalendar-monthContainer': {
                  minHeight: '200px', // Increased height to show 6 rows comfortably
                  display: 'grid',
                  gridTemplateRows: 'repeat(6, 1fr)',
                  overflow: 'visible', // Ensure content is not cut off
                },
                '& .MuiPickersCalendarHeader-root': {
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingTop: 1,
                  marginTop: 0,
                },
                '& .MuiPickersCalendarHeader-label': {
                  marginBottom: isMobile ? 1 : 0,
                },
                // Hide the "Select date" text on mobile/tablet
                '& .MuiPickersLayout-toolbar': {
                  display: 'none',
                },
                // Improve year selector contrast
                '& .MuiYearCalendar-root': {
                  '& .Mui-selected': {
                    backgroundColor: '#4F46E5 !important',
                    color: '#ffffff !important',
                  },
                  '& .MuiYearCalendar-yearButton': {
                    '&:hover': {
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#4338CA !important',
                    },
                  },
                },
                // Ensure the paper has enough height
                '& .MuiPaper-root': {
                  minHeight: '400px',
                },
              },
            },
            popper: {
              sx: {
                zIndex: 1300,
              },
            },
          }}
          slots={{
            actionBar: CustomActionBar,
          }}
        />
      ) : (
        <TextField
          label={label}
          type="text"
          disabled={disabled}
          value={selectedDate ? dayjs(selectedDate).format('MM-DD-YYYY') : ''}
          sx={{
            backgroundColor: 'white',
            borderRadius: '4px',
            cursor: 'default',
            float: 'inherit',
          }}
          // InputProps={{
          //   readOnly: true,
          // }}
        />
      )}
      {required && !isFilled && (
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          Required
        </Typography>
      )}
      {errorMessage && (
        <Typography fontSize={12} color="red" ml={0.5}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  )
}

export default CustomDatePicker
